import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { Disposable } from '@patsnap/tahiti-core';
import {
    OnboardingActionGuideRuntime,
    type OnboardingActionGuideRuntime as OnboardingActionGuideRuntimeType,
} from '../onboarding-action-guide-runtime';
import type {
    OnboardingActionGuideOptions,
    OnboardingActionGuideService,
} from '../onboarding-action-guide-service';

/**
 * 通用按钮操作引导的生命周期管理器。
 *
 * 负责去重目标、添加视觉状态、动态挂载手型，并通过单个 Disposable 对外提供完整清理能力。
 */
@injectable()
export class OnboardingActionGuideServiceImpl implements OnboardingActionGuideService {
    constructor(
        /** 封装目标样式和手型 Vue 子应用的浏览器操作。 */
        @inject(OnboardingActionGuideRuntime)
        private readonly _runtime: OnboardingActionGuideRuntimeType,
    ) {}

    /**
     * 为一个或多个目标展示统一的操作引导。
     * @param targets 需要同时显示呼吸效果和点击手型的目标元素。
     * @param options 可选视觉配置。
     * @returns 可幂等清理全部目标引导的句柄。
     */
    show(targets: readonly Element[], options: OnboardingActionGuideOptions = {}): Disposable {
        // 同一个元素只挂载一次，避免重复手型和重复波纹叠加。
        /** 去重后的实际挂载目标。 */
        const uniqueTargets = Array.from(new Set(targets));
        /** 每个目标内部动态手型对应的卸载句柄。 */
        const guideHandDisposables: Disposable[] = [];
        /** 防止同一个清理句柄被重复执行。 */
        let isDisposed = false;

        try {
            uniqueTargets.forEach((target) => {
                this._runtime.addTargetClass(target);
                if (options.showHand !== false) {
                    guideHandDisposables.push(this._runtime.mountGuideHand(target));
                }
            });
        } catch (error) {
            // 部分目标挂载失败时回滚全部已完成操作，不留下孤立节点或样式。
            guideHandDisposables.forEach(disposable => disposable.dispose());
            uniqueTargets.forEach(target => this._runtime.removeTargetClass(target));
            throw error;
        }

        return {
            dispose: () => {
                if (isDisposed) return;
                isDisposed = true;
                guideHandDisposables.forEach(disposable => disposable.dispose());
                uniqueTargets.forEach(target => this._runtime.removeTargetClass(target));
            },
        };
    }
}
